import { API_CONFIG } from '../config/api';
import { authService } from './auth';

// Simple HTTP client for API calls with authentication
const httpClient = {
  // Cache for in-flight requests to prevent duplicate calls
  inFlightRequests: new Map<string, Promise<any>>(),
  
  async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_CONFIG.API_URL}${endpoint}`;
    console.log('Admin Portal - Making HTTP request to:', url);
    console.log('Admin Portal - API_CONFIG.API_URL:', API_CONFIG.API_URL);
    console.log('Admin Portal - Endpoint:', endpoint);
    console.log('Admin Portal - Request options:', options);
    
    // Create a unique key for this request
    const requestKey = `${options.method || 'GET'}:${url}`;
    
    // Check if this request is already in flight
    if (this.inFlightRequests.has(requestKey)) {
      console.log('Request already in flight, waiting for existing request:', requestKey);
      return this.inFlightRequests.get(requestKey);
    }
    
    // Get authentication token
    let headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add any additional headers from options
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }
    
    let currentUser;
    try {
      currentUser = await authService.getCurrentUser();
      // Add Bearer token if user is authenticated
      if (currentUser && currentUser.access_token) {
        headers['Authorization'] = `Bearer ${currentUser.access_token}`;
      }
    } catch (error) {
      // User not authenticated, continue without token
      console.log('No authentication token available');
    }
    
    // Create the request promise
    const requestPromise = (async () => {
      let response = await fetch(url, {
        headers,
        ...options,
      });
      
      // Handle 401 Unauthorized - try to refresh token
      if (response.status === 401 && currentUser) {
        try {
          console.log('Token expired, attempting to refresh...');
          const newToken = await authService.refreshToken();
          headers['Authorization'] = `Bearer ${newToken}`;
          
          // Retry the request with new token
          response = await fetch(url, {
            headers,
            ...options,
          });
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Force logout and redirect to login
          await authService.signOut();
          window.location.href = '/';
          throw new Error('Authentication required');
        }
      }
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        console.error('HTTP error response:', response.status, response.statusText);
        if (response.status === 401) {
          // Force logout and redirect to login for any 401 response
          await authService.signOut();
          window.location.href = '/';
          throw new Error('Authentication required');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Handle 204 No Content responses (common for DELETE operations)
      if (response.status === 204) {
        console.log('Response: 204 No Content');
        return null;
      }
      
      // Check if response has content before parsing JSON
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      // If there's no content or content-length is 0, return null
      if (contentLength === '0') {
        return null;
      }
      
      // Try to parse as JSON, but handle empty responses gracefully
      try {
        const text = await response.text();
        if (!text || text.trim() === '') {
          return null;
        }
        // Try to parse as JSON
        const responseData = JSON.parse(text);
        console.log('Response data:', responseData);
        return responseData;
      } catch (parseError) {
        // If parsing fails and we expected JSON, it might be an empty response
        if (contentType?.includes('application/json')) {
          console.warn('Failed to parse JSON response, returning null');
          return null;
        }
        // For non-JSON content types, return null
        return null;
      }
    })();
    
    // Store the promise in the in-flight requests map
    this.inFlightRequests.set(requestKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the in-flight request
      this.inFlightRequests.delete(requestKey);
    }
  },

  get: (endpoint: string, params?: Record<string, any>) => {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }
    return httpClient.request(url);
  },

  post: (endpoint: string, data?: any) => {
    return httpClient.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  put: (endpoint: string, data?: any) => {
    return httpClient.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: (endpoint: string) => {
    return httpClient.request(endpoint, {
      method: 'DELETE',
    });
  },

  /**
   * Fetch a binary response (e.g. PDF download) with the same auth/refresh behavior.
   */
  getBlob: async (endpoint: string): Promise<{ blob: Blob; filename: string | null }> => {
    const url = `${API_CONFIG.API_URL}${endpoint}`;
    let headers: Record<string, string> = {};

    let currentUser;
    try {
      currentUser = await authService.getCurrentUser();
      if (currentUser && currentUser.access_token) {
        headers['Authorization'] = `Bearer ${currentUser.access_token}`;
      }
    } catch (error) {
      console.log('No authentication token available');
    }

    let response = await fetch(url, { headers });

    if (response.status === 401 && currentUser) {
      try {
        const newToken = await authService.refreshToken();
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { headers });
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        await authService.signOut();
        window.location.href = '/';
        throw new Error('Authentication required');
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        await authService.signOut();
        window.location.href = '/';
        throw new Error('Authentication required');
      }
      let detail = `HTTP error! status: ${response.status}`;
      try {
        const errBody = await response.json();
        if (errBody?.detail) {
          detail = typeof errBody.detail === 'string' ? errBody.detail : detail;
        }
      } catch {
        // ignore non-JSON error bodies
      }
      throw new Error(detail);
    }

    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = filenameMatch ? filenameMatch[1] : null;
    const blob = await response.blob();
    return { blob, filename };
  },
};

export const api = {
  auth: {
    // Note: Authentication is now handled by the authService directly
    // This endpoint is kept for backward compatibility but should use authService.signIn()
    signIn: async (email: string, password: string) => {
      try {
        return httpClient.post('/api/v1/auth/login', { email, password });
      } catch (error) {
        console.error('Auth error:', error);
        throw error;
      }
    }
  },

  providers: {
    list: async (skip: number = 0, limit: number = 100) => {
      try {
        return httpClient.get(`/api/v1/providers?skip=${skip}&limit=${limit}`);
      } catch (error) {
        console.error('Provider error:', error);
        throw error;
      }
    },

    getAll: async () => {
      try {
        return httpClient.get('/api/v1/providers?limit=1000');
      } catch (error) {
        console.error('Provider error:', error);
        throw error;
      }
    },

    get: async (id: number | string) => {
      try {
        return httpClient.get(`/api/v1/providers/${id}`);
      } catch (error) {
        console.error('Provider error:', error);
        throw error;
      }
    },

    getByEmail: async (email: string) => {
      try {
        return httpClient.get('/api/v1/providers', { email });
      } catch (error) {
        console.error('Provider error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        console.log('Sending provider creation request with data:', data);
        const result = await httpClient.post('/api/v1/providers', data);
        console.log('Provider creation API response:', result);
        return result;
      } catch (error) {
        console.error('Provider creation error:', error);
        throw error;
      }
    },

    update: async (id: number | string, data: any) => {
      try {
        return httpClient.put(`/api/v1/providers/${id}`, data);
      } catch (error) {
        console.error('Provider error:', error);
        throw error;
      }
    },

    delete: async (id: number | string) => {
      try {
        return httpClient.delete(`/api/v1/providers/${id}`);
      } catch (error) {
        console.error('Provider error:', error);
        throw error;
      }
    }
  },

  billingAgents: {
    getAll: async () => httpClient.get('/api/v1/billing-agents'),
    get: async (id: number) => httpClient.get(`/api/v1/billing-agents/${id}`),
    create: async (data: any) => httpClient.post('/api/v1/billing-agents', data),
    update: async (id: number, data: any) => httpClient.put(`/api/v1/billing-agents/${id}`, data),
    delete: async (id: number) => httpClient.delete(`/api/v1/billing-agents/${id}`),
    setProviders: async (id: number, providerIds: number[]) =>
      httpClient.put(`/api/v1/billing-agents/${id}/providers`, { provider_ids: providerIds }),
    getAuditLog: async (id: number) => httpClient.get(`/api/v1/billing-agents/${id}/provider-audit`),
  },

  providerVehicles: {
    list: async (params?: { providerId?: number; includeInactive?: boolean }) => {
      const sp = new URLSearchParams();
      if (params?.providerId != null) sp.set('provider_id', String(params.providerId));
      if (params?.includeInactive) sp.set('include_inactive', 'true');
      const qs = sp.toString();
      return httpClient.get(`/api/v1/provider-vehicles${qs ? `?${qs}` : ''}`);
    },
    get: async (id: number) => httpClient.get(`/api/v1/provider-vehicles/${id}`),
    create: async (data: import('../types').ProviderVehicleCreate) =>
      httpClient.post('/api/v1/provider-vehicles', data),
    update: async (id: number, data: import('../types').ProviderVehicleUpdate) =>
      httpClient.put(`/api/v1/provider-vehicles/${id}`, data),
    delete: async (id: number) => httpClient.delete(`/api/v1/provider-vehicles/${id}`),
  },

  clients: {
    list: async (skip: number = 0, limit: number = 100) => {
      try {
        return httpClient.get(`/api/v1/clients?skip=${skip}&limit=${limit}`);
      } catch (error) {
        console.error('Client error:', error);
        throw error;
      }
    },
    listByProvider: async (providerId: number | string, limit: number = 100) => {
      try {
        return httpClient.get(`/api/v1/clients/provider/${providerId}?limit=${limit}`);
      } catch (error) {
        console.error('Error fetching provider clients:', error);
        throw error;
      }
    },

    get: async (id: number | string) => {
      try {
        return httpClient.get(`/api/v1/clients/${id}`);
      } catch (error) {
        console.error('Client error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/clients', data);
      } catch (error) {
        console.error('Client error:', error);
        throw error;
      }
    },

    update: async (id: number | string, data: any) => {
      try {
        return httpClient.put(`/api/v1/clients/${id}`, data);
      } catch (error) {
        console.error('Client error:', error);
        throw error;
      }
    },

    delete: async (id: number | string) => {
      try {
        return httpClient.delete(`/api/v1/clients/${id}`);
      } catch (error) {
        console.error('Client error:', error);
        throw error;
      }
    }
  },

        visits: {
      list: async (skip: number = 0, limit: number = 10, filters?: {
        startDate?: string;
        endDate?: string;
        clientId?: number;
        status?: string;
      }) => {
        try {
          const params = new URLSearchParams({
            skip: skip.toString(),
            limit: limit.toString()
          });
          
          // Add filter parameters if provided
          if (filters?.startDate) {
            params.append('start_date', filters.startDate);
          }
          if (filters?.endDate) {
            params.append('end_date', filters.endDate);
          }
          if (filters?.clientId) {
            params.append('client_id', filters.clientId.toString());
          }
          if (filters?.status) {
            params.append('status', filters.status);
          }
          
          return httpClient.get(`/api/v1/visits?${params.toString()}`);
        } catch (error) {
          console.error('Visit error:', error);
          throw error;
        }
      },
      listByProvider: async (providerId: number | string, limit: number = 10, skip: number = 0, filters?: {
        startDate?: string;
        endDate?: string;
        clientId?: number;
        status?: string;
      }) => {
        try {
          const params = new URLSearchParams({
            skip: skip.toString(),
            limit: limit.toString()
          });
          
          // Add filter parameters if provided
          if (filters?.startDate) {
            params.append('start_date', filters.startDate);
          }
          if (filters?.endDate) {
            params.append('end_date', filters.endDate);
          }
          if (filters?.clientId) {
            params.append('client_id', filters.clientId.toString());
          }
          if (filters?.status) {
            params.append('status', filters.status);
          }
          
          return httpClient.get(`/api/v1/visits/provider/${providerId}?${params.toString()}`);
        } catch (error) {
          console.error('Error fetching provider visits:', error);
          throw error;
        }
      },

    get: async (id: number) => {
      try {
        return httpClient.get(`/api/v1/visits/${id}`);
      } catch (error) {
        console.error('Visit error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/visits', data);
      } catch (error) {
        console.error('Visit error:', error);
        throw error;
      }
    },

    update: async (id: number, data: any) => {
      try {
        return httpClient.put(`/api/v1/visits/${id}`, data);
      } catch (error) {
        console.error('Visit error:', error);
        throw error;
      }
    },

    delete: async (id: number) => {
      try {
        return httpClient.delete(`/api/v1/visits/${id}`);
      } catch (error) {
        console.error('Visit error:', error);
        throw error;
      }
    },

    getWeeklyHours: async (providerId: number, scheduledStart: string) => {
      try {
        if (!scheduledStart) {
          throw new Error('Scheduled start date is required');
        }
        
        // Convert date to the format expected by the backend: YYYY-MM-DD HH:MM:SS or YYYY-MM-DD
        let formattedDate: string;
        try {
          const date = new Date(scheduledStart);
          if (isNaN(date.getTime())) {
            throw new Error('Invalid date');
          }
          
          // Format as YYYY-MM-DD HH:MM:SS
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          
          formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } catch (error) {
          // If parsing fails, try to use the string as-is (might already be in correct format)
          formattedDate = scheduledStart.trim();
        }
        
        if (!formattedDate) {
          throw new Error('Scheduled start date cannot be empty');
        }
        
        console.log('Getting weekly hours for provider:', providerId, 'with scheduled_start:', formattedDate);
        
        // Manually construct query string to ensure it's properly encoded
        const params = new URLSearchParams();
        params.append('scheduled_start', formattedDate);
        const queryString = params.toString();
        
        return httpClient.get(`/api/v1/visits/provider/${providerId}/weekly-hours?${queryString}`);
      } catch (error) {
        console.error('Weekly hours error:', error);
        throw error;
      }
    }
  },

  visitParents: {
    list: async (
      skip: number = 0,
      limit: number = 10,
      filters?: {
        providerId?: number;
        startDate?: string;
        endDate?: string;
        clientId?: number;
        status?: string;
      }
    ) => {
      const params = new URLSearchParams({
        skip: String(skip),
        limit: String(limit),
      });
      if (filters?.providerId != null) params.append('provider_id', String(filters.providerId));
      if (filters?.startDate) params.append('start_date', filters.startDate);
      if (filters?.endDate) params.append('end_date', filters.endDate);
      if (filters?.clientId != null) params.append('client_id', String(filters.clientId));
      if (filters?.status) params.append('status', filters.status);
      return httpClient.get(`/api/v1/visit-parents?${params.toString()}`);
    },
    delete: async (visitParentId: number) => {
      return httpClient.delete(`/api/v1/visit-parents/${visitParentId}`);
    },
    get: async (visitParentId: number) => {
      return httpClient.get(`/api/v1/visit-parents/${visitParentId}`);
    },
    create: async (data: { provider_id: number }) => {
      return httpClient.post('/api/v1/visit-parents', data);
    },
  },

  transportationParents: {
    list: async (
      skip: number = 0,
      limit: number = 100,
      filters?: {
        providerId?: number;
        startDate?: string;
        endDate?: string;
        clientId?: number;
      }
    ) => {
      const params = new URLSearchParams({
        skip: String(skip),
        limit: String(limit),
      });
      if (filters?.providerId != null) params.append('provider_id', String(filters.providerId));
      if (filters?.startDate) params.append('start_date', filters.startDate);
      if (filters?.endDate) params.append('end_date', filters.endDate);
      if (filters?.clientId != null) params.append('client_id', String(filters.clientId));
      return httpClient.get(`/api/v1/transportation-parents?${params.toString()}`);
    },
    create: async (data: { provider_id: number }) => {
      return httpClient.post('/api/v1/transportation-parents', data);
    },
    delete: async (transportationParentId: number) => {
      return httpClient.delete(`/api/v1/transportation-parents/${transportationParentId}`);
    },
    get: async (transportationParentId: number) => {
      return httpClient.get(`/api/v1/transportation-parents/${transportationParentId}`);
    },
  },

  services: {
    list: async (skip: number = 0, limit: number = 100) => {
      try {
        return httpClient.get(`/api/v1/services?skip=${skip}&limit=${limit}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    get: async (id: number | string) => {
      try {
        return httpClient.get(`/api/v1/services/${id}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getByCategory: async (category: string) => {
      try {
        return httpClient.get(`/api/v1/services/category/${category}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getByState: async (state: string) => {
      try {
        return httpClient.get(`/api/v1/services/state/${state}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getByCounty: async (county: string) => {
      try {
        return httpClient.get(`/api/v1/services/county/${county}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getByMunicipality: async (municipality: string) => {
      try {
        return httpClient.get(`/api/v1/services/municipality/${municipality}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getBySubregion: async (subregion: string) => {
      try {
        return httpClient.get(`/api/v1/services/subregion/${subregion}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getByProgram: async (program: string) => {
      try {
        return httpClient.get(`/api/v1/services/program/${program}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getByServiceUnit: async (serviceUnit: string) => {
      try {
        return httpClient.get(`/api/v1/services/service-unit/${serviceUnit}`);
      } catch (error) {
        console.error('Service error:', error);
        throw error;
      }
    },

    getTransportationServices: async () => {
      try {
        return httpClient.get('/api/v1/services/transportation/');
      } catch (error) {
        console.error('Transportation services error:', error);
        throw error;
      }
    }
  },

  clientIspServices: {
    list: async (skip: number = 0, limit: number = 100) => {
      try {
        return httpClient.get(`/api/v1/client-isp-services?skip=${skip}&limit=${limit}`);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    },

    getByClient: async (clientId: number | string) => {
      try {
        return httpClient.get(`/api/v1/client-isp-services/client/${clientId}`);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    },

    getByProvider: async (providerId: number | string) => {
      try {
        return httpClient.get(`/api/v1/client-isp-services/provider/${providerId}`);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    },

    getByIsp: async (ispId: number | string) => {
      try {
        return httpClient.get(`/api/v1/client-isp-services/isp/${ispId}`);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    },

    getByClientAndProvider: async (clientId: number | string, providerId: number | string) => {
      try {
        return httpClient.get(`/api/v1/client-isp-services/client/${clientId}/provider/${providerId}`);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    },

    getServicesForClientProvider: async (clientId: number | string, providerId: number | string) => {
      try {
        return httpClient.get(`/api/v1/client-isp-services/client/${clientId}/provider/${providerId}/services`);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    },

    getTransportationServicesForClientProvider: async (clientId: number | string, providerId: number | string) => {
      try {
        return httpClient.get(`/api/v1/client-isp-services/client/${clientId}/provider/${providerId}/transportation`);
      } catch (error) {
        console.error('Client ISP transportation services error:', error);
        throw error;
      }
    },

    /** Client IDs with at least one active ISP service whose service title contains "Transportation". */
    getClientIdsWithTransportationIsp: async (providerId: number | string): Promise<number[]> => {
      try {
        const data = await httpClient.get(
          `/api/v1/client-isp-services/provider/${providerId}/clients-with-transportation`
        );
        return Array.isArray(data) ? data.map(Number) : [];
      } catch (error) {
        console.error('Client ISP transportation-eligible clients error:', error);
        return [];
      }
    },

    create: async (data: any) => {
      try {
        console.log('Creating client ISP service with data:', data);
        const result = await httpClient.post('/api/v1/client-isp-services', data);
        console.log('Client ISP service creation result:', result);
        return result;
      } catch (error) {
        console.error('Client ISP services creation error:', error);
        if (error instanceof Error) {
          console.error('Error details:', error.message);
        }
        throw error;
      }
    },

    update: async (id: number, data: any) => {
      try {
        return httpClient.put(`/api/v1/client-isp-services/${id}`, data);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    },

    delete: async (id: number) => {
      try {
        return httpClient.delete(`/api/v1/client-isp-services/${id}`);
      } catch (error) {
        console.error('Client ISP services error:', error);
        throw error;
      }
    }
  },

  rates: {
    list: async (skip: number = 0, limit: number = 100, filters?: {
      service_id?: number | string;
      CODB?: string;
      group_size?: number;
      ODDP_range?: number;
      daily_rate_flag?: boolean;
      active_from?: string;
      active_to?: string;
    }) => {
      try {
        const params = { skip, limit, ...filters };
        return httpClient.get('/api/v1/rates', params);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    get: async (id: number) => {
      try {
        return httpClient.get(`/api/v1/rates/${id}`);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    getByService: async (serviceId: number | string) => {
      try {
        return httpClient.get(`/api/v1/rates/service/${serviceId}`);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    getByCODB: async (CODB: string) => {
      try {
        return httpClient.get(`/api/v1/rates/codb/${CODB}`);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    getByGroupSize: async (groupSize: number) => {
      try {
        return httpClient.get(`/api/v1/rates/group/${groupSize}`);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    getByODDPRange: async (ODDPRange: number) => {
      try {
        return httpClient.get(`/api/v1/rates/oddp/${ODDPRange}`);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/rates', data);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    update: async (id: number, data: any) => {
      try {
        return httpClient.put(`/api/v1/rates/${id}`, data);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    },

    delete: async (id: number) => {
      try {
        return httpClient.delete(`/api/v1/rates/${id}`);
      } catch (error) {
        console.error('Rate error:', error);
        throw error;
      }
    }
  },

  transportation: {
    list: async () => {
      try {
        return httpClient.get('/api/v1/transportation');
      } catch (error) {
        console.error('Transportation error:', error);
        throw error;
      }
    },

    get: async (id: number) => {
      try {
        return httpClient.get(`/api/v1/transportation/${id}`);
      } catch (error) {
        console.error('Transportation error:', error);
        throw error;
      }
    },

    dimensions: async () => {
      try {
        return httpClient.get('/api/v1/transportation/dimensions');
      } catch (error) {
        console.error('Transportation dimensions error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/transportation', data);
      } catch (error) {
        console.error('Transportation error:', error);
        throw error;
      }
    },

    update: async (id: number, data: any) => {
      try {
        return httpClient.put(`/api/v1/transportation/${id}`, data);
      } catch (error) {
        console.error('Transportation error:', error);
        throw error;
      }
    },

    delete: async (id: number) => {
      try {
        return httpClient.delete(`/api/v1/transportation/${id}`);
      } catch (error) {
        console.error('Transportation error:', error);
        throw error;
      }
    }
  },

  locations: {
    list: async () => {
      try {
        return httpClient.get('/api/v1/locations');
      } catch (error) {
        console.error('Location error:', error);
        throw error;
      }
    },

    /** Locations used in this provider's transportation, most-used first (server). */
    listByProvider: async (providerId: number) => {
      try {
        return httpClient.get(`/api/v1/locations/by-provider/${providerId}`);
      } catch (error) {
        console.error('Location error:', error);
        throw error;
      }
    },

    get: async (id: number | string) => {
      try {
        return httpClient.get(`/api/v1/locations/${id}`);
      } catch (error) {
        console.error('Location error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/locations/', data);
      } catch (error) {
        console.error('Location error:', error);
        throw error;
      }
    },

    update: async (id: number | string, data: any) => {
      try {
        return httpClient.put(`/api/v1/locations/${id}`, data);
      } catch (error) {
        console.error('Location error:', error);
        throw error;
      }
    },

    delete: async (id: number | string) => {
      try {
        return httpClient.delete(`/api/v1/locations/${id}`);
      } catch (error) {
        console.error('Location error:', error);
        throw error;
      }
    }
  },

  addresses: {
    validate: async (data: {
      address_line1?: string;
      address_line2?: string;
      city?: string;
      state?: string;
      zip_code?: string;
      address_lines?: string[];
    }) => {
      try {
        return httpClient.post('/api/v1/addresses/validate', data);
      } catch (error) {
        console.error('Address validate error:', error);
        throw error;
      }
    },

    reverseGeocode: async (data: { latitude: number; longitude: number }) => {
      try {
        return httpClient.post('/api/v1/addresses/reverse-geocode', data);
      } catch (error) {
        console.error('Reverse geocode error:', error);
        throw error;
      }
    },
  },

  outcomes: {
    list: async () => {
      try {
        return httpClient.get('/api/v1/outcomes');
      } catch (error) {
        console.error('Outcomes error:', error);
        throw error;
      }
    },

    getByClient: async (clientId: number | string, includeInactive: boolean = false) => {
      try {
        const url = `/api/v1/outcomes/client/${clientId}${includeInactive ? '?include_inactive=true' : ''}`;
        return httpClient.get(url);
      } catch (error) {
        console.error('Outcomes error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/outcomes', data);
      } catch (error) {
        console.error('Outcomes error:', error);
        throw error;
      }
    },

    update: async (id: number, data: any) => {
      try {
        return httpClient.put(`/api/v1/outcomes/${id}`, data);
      } catch (error) {
        console.error('Outcomes error:', error);
        throw error;
      }
    },

    delete: async (id: number) => {
      try {
        return httpClient.delete(`/api/v1/outcomes/${id}`);
      } catch (error) {
        console.error('Outcomes error:', error);
        throw error;
      }
    }
  },

  experiences: {
    list: async () => {
      try {
        return httpClient.get('/api/v1/experiences');
      } catch (error) {
        console.error('Experiences error:', error);
        throw error;
      }
    },

    getByClient: async (clientId: number | string, includeInactive: boolean = false) => {
      try {
        const url = `/api/v1/experiences/client/${clientId}${includeInactive ? '?include_inactive=true' : ''}`;
        return httpClient.get(url);
      } catch (error) {
        console.error('Experiences error:', error);
        throw error;
      }
    },

    getByOutcome: async (outcomeId: number) => {
      try {
        return httpClient.get(`/api/v1/experiences/outcome/${outcomeId}`);
      } catch (error) {
        console.error('Experiences error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/experiences', data);
      } catch (error) {
        console.error('Experiences error:', error);
        throw error;
      }
    },

    update: async (id: number, data: any) => {
      try {
        return httpClient.put(`/api/v1/experiences/${id}`, data);
      } catch (error) {
        console.error('Experiences error:', error);
        throw error;
      }
    },

    delete: async (id: number) => {
      try {
        return httpClient.delete(`/api/v1/experiences/${id}`);
      } catch (error) {
        console.error('Experiences error:', error);
        throw error;
      }
    }
  },

  unusualIncidentTypes: {
    list: async () => {
      try {
        return httpClient.get('/api/v1/unusual-incidents');
      } catch (error) {
        console.error('Unusual incident types error:', error);
        throw error;
      }
    },

    get: async (id: number) => {
      try {
        return httpClient.get(`/api/v1/unusual-incidents/${id}`);
      } catch (error) {
        console.error('Unusual incident types error:', error);
        throw error;
      }
    }
  },

  clientISP: {
    list: async (providerId?: number, clientId?: number, activeStatus?: boolean) => {
      try {
        const params = new URLSearchParams();
        if (providerId) params.append('provider_id', providerId.toString());
        if (clientId) params.append('client_id', clientId.toString());
        if (activeStatus !== undefined) params.append('active_status', activeStatus.toString());
        const query = params.toString();
        return httpClient.get(`/api/v1/client-isp${query ? `?${query}` : ''}`);
      } catch (error) {
        console.error('Client ISP error:', error);
        throw error;
      }
    },

    getByClient: async (clientId: number | string, activeStatus?: boolean) => {
      try {
        const params = new URLSearchParams();
        if (activeStatus !== undefined) params.append('active_status', activeStatus.toString());
        const query = params.toString();
        // Omit active_status to return both active and inactive ISPs for the client.
        return httpClient.get(`/api/v1/client-isp/client/${clientId}${query ? `?${query}` : ''}`);
      } catch (error) {
        console.error('Client ISP error:', error);
        throw error;
      }
    },

    /**
     * Get ISPs for visit entry, filtered by provider, client, and visit date span.
     * Includes inactive ISPs so admins can attach visits to historical plans.
     */
    listForVisit: async (
      providerId: number | string,
      clientId: number | string,
      visitDate?: string | null,
      options?: { includeInactive?: boolean }
    ) => {
      try {
        const includeInactive = options?.includeInactive !== false;
        if (includeInactive) {
          // List all ISPs for provider/client, then apply the same date-span filter as /for-visit.
          const rows = await httpClient.get(
            `/api/v1/client-isp?provider_id=${providerId}&client_id=${clientId}`
          );
          const all = Array.isArray(rows) ? rows : [];
          if (!visitDate) return all;
          const datePart = visitDate.split('T')[0] || visitDate.split(' ')[0];
          if (!datePart) return all;
          return all.filter((isp: { span_start_date?: string | null; span_end_date?: string | null }) => {
            const start = isp.span_start_date ? String(isp.span_start_date).split('T')[0] : null;
            const end = isp.span_end_date ? String(isp.span_end_date).split('T')[0] : null;
            if (start && datePart < start) return false;
            if (end && datePart > end) return false;
            return true;
          });
        }
        const params = new URLSearchParams();
        params.append('provider_id', providerId.toString());
        params.append('client_id', clientId.toString());
        if (visitDate) {
          const datePart = visitDate.split('T')[0];
          if (datePart) params.append('visit_date', datePart);
        }
        return httpClient.get(`/api/v1/client-isp/for-visit?${params.toString()}`);
      } catch (error) {
        console.error('Client ISP listForVisit error:', error);
        throw error;
      }
    },

    get: async (ispId: number) => {
      try {
        return httpClient.get(`/api/v1/client-isp/${ispId}`);
      } catch (error) {
        console.error('Client ISP error:', error);
        throw error;
      }
    },

    create: async (data: any) => {
      try {
        return httpClient.post('/api/v1/client-isp', data);
      } catch (error) {
        console.error('Client ISP error:', error);
        throw error;
      }
    },

    update: async (ispId: number, data: any) => {
      try {
        return httpClient.put(`/api/v1/client-isp/${ispId}`, data);
      } catch (error) {
        console.error('Client ISP error:', error);
        throw error;
      }
    },

    delete: async (ispId: number) => {
      try {
        return httpClient.delete(`/api/v1/client-isp/${ispId}`);
      } catch (error) {
        console.error('Client ISP error:', error);
        throw error;
      }
    },

    outcomes: {
      list: async (ispId: number, activeStatus?: boolean) => {
        try {
          const params = new URLSearchParams();
          if (activeStatus !== undefined) params.append('active_status', activeStatus.toString());
          const qs = params.toString();
          return httpClient.get(`/api/v1/client-isp/${ispId}/outcomes${qs ? `?${qs}` : ''}`);
        } catch (error) {
          console.error('Client ISP Outcomes error:', error);
          throw error;
        }
      },

      create: async (ispId: number, data: any) => {
        try {
          return httpClient.post(`/api/v1/client-isp/${ispId}/outcomes`, data);
        } catch (error) {
          console.error('Client ISP Outcomes error:', error);
          throw error;
        }
      },

      update: async (outcomeId: number, data: any) => {
        try {
          return httpClient.put(`/api/v1/client-isp/outcomes/${outcomeId}`, data);
        } catch (error) {
          console.error('Client ISP Outcomes error:', error);
          throw error;
        }
      },

      delete: async (outcomeId: number) => {
        try {
          return httpClient.delete(`/api/v1/client-isp/outcomes/${outcomeId}`);
        } catch (error) {
          console.error('Client ISP Outcomes error:', error);
          throw error;
        }
      }
    },

    experiences: {
      list: async (ispId: number, outcomeId?: number, activeStatus?: boolean) => {
        try {
          const params = new URLSearchParams();
          if (outcomeId !== undefined) params.append('outcome_id', outcomeId.toString());
          if (activeStatus !== undefined) params.append('active_status', activeStatus.toString());
          const qs = params.toString();
          return httpClient.get(`/api/v1/client-isp/${ispId}/experiences${qs ? `?${qs}` : ''}`);
        } catch (error) {
          console.error('Client ISP Experiences error:', error);
          throw error;
        }
      },

      create: async (ispId: number, data: any) => {
        try {
          return httpClient.post(`/api/v1/client-isp/${ispId}/experiences`, data);
        } catch (error) {
          console.error('Client ISP Experiences error:', error);
          throw error;
        }
      },

      update: async (experienceId: number, data: any) => {
        try {
          return httpClient.put(`/api/v1/client-isp/experiences/${experienceId}`, data);
        } catch (error) {
          console.error('Client ISP Experiences error:', error);
          throw error;
        }
      },

      delete: async (experienceId: number) => {
        try {
          return httpClient.delete(`/api/v1/client-isp/experiences/${experienceId}`);
        } catch (error) {
          console.error('Client ISP Experiences error:', error);
          throw error;
        }
      }
    },

    supports: {
      list: async (ispId: number, activeStatus?: boolean) => {
        try {
          const params = new URLSearchParams();
          if (activeStatus !== undefined) params.append('active_status', activeStatus.toString());
          const qs = params.toString();
          return httpClient.get(`/api/v1/client-isp/${ispId}/supports${qs ? `?${qs}` : ''}`);
        } catch (error) {
          console.error('Client ISP Supports error:', error);
          throw error;
        }
      },

      create: async (ispId: number, data: any) => {
        try {
          return httpClient.post(`/api/v1/client-isp/${ispId}/supports`, data);
        } catch (error) {
          console.error('Client ISP Supports error:', error);
          throw error;
        }
      },

      update: async (supportId: number, data: any) => {
        try {
          return httpClient.put(`/api/v1/client-isp/supports/${supportId}`, data);
        } catch (error) {
          console.error('Client ISP Supports error:', error);
          throw error;
        }
      },

      delete: async (supportId: number) => {
        try {
          return httpClient.delete(`/api/v1/client-isp/supports/${supportId}`);
        } catch (error) {
          console.error('Client ISP Supports error:', error);
          throw error;
        }
      }
    },

    // Helper method to get outcomes/experiences/supports filtered by visit date
    getForVisit: async (clientId: number, visitDate: string) => {
      try {
        // Get all ISPs (active and inactive) that span the visit date
        const isps = await httpClient.get(`/api/v1/client-isp/client/${clientId}`);
        const allISPs = Array.isArray(isps) ? isps : [];
        
        // Filter ISPs by date span
        const visitDateObj = new Date(visitDate);
        const validISPs = allISPs.filter((isp: any) => {
          const startDate = isp.span_start_date ? new Date(isp.span_start_date) : null;
          const endDate = isp.span_end_date ? new Date(isp.span_end_date) : null;
          
          if (startDate && visitDateObj < startDate) return false;
          if (endDate && visitDateObj > endDate) return false;
          return true;
        });

        // Fetch outcomes, experiences, and supports for valid ISPs
        const outcomes: any[] = [];
        const experiences: any[] = [];
        const supports: any[] = [];

        for (const isp of validISPs) {
          try {
            const [ispOutcomes, ispExperiences, ispSupports] = await Promise.all([
              httpClient.get(`/api/v1/client-isp/${isp.isp_id}/outcomes?active_status=true`),
              httpClient.get(`/api/v1/client-isp/${isp.isp_id}/experiences?active_status=true`),
              httpClient.get(`/api/v1/client-isp/${isp.isp_id}/supports?active_status=true`)
            ]);
            
            outcomes.push(...(Array.isArray(ispOutcomes) ? ispOutcomes : []));
            experiences.push(...(Array.isArray(ispExperiences) ? ispExperiences : []));
            supports.push(...(Array.isArray(ispSupports) ? ispSupports : []));
          } catch (err) {
            console.error(`Error fetching ISP ${isp.isp_id} data:`, err);
          }
        }

        return { outcomes, experiences, supports };
      } catch (error) {
        console.error('Client ISP getForVisit error:', error);
        throw error;
      }
    }
  },

  billing: {
    getProviderBilling: async (providerId: number, startDate?: string, endDate?: string) => {
      try {
        const params: any = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        return httpClient.get(`/api/v1/billing/${providerId}`, params);
      } catch (error) {
        console.error('Billing error:', error);
        throw error;
      }
    },

    getProviderBillingSummary: async (providerId: number, startDate?: string, endDate?: string) => {
      try {
        const params: any = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        return httpClient.get(`/api/v1/billing/${providerId}/summary`, params);
      } catch (error) {
        console.error('Billing summary error:', error);
        throw error;
      }
    },

    exportProviderBilling: async (providerId: number, startDate?: string, endDate?: string, format: string = 'txt') => {
      try {
        const params: any = { format };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        return httpClient.get(`/api/v1/billing/${providerId}/export`, params);
      } catch (error) {
        console.error('Billing export error:', error);
        throw error;
      }
    },

    getProviderBillingSummaryDetails: async (providerId: number, startDate?: string, endDate?: string, clientId?: number, serviceCode?: string) => {
      try {
        const params: any = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (clientId) params.client_id = clientId;
        if (serviceCode) params.service_code = serviceCode;
        return httpClient.get(`/api/v1/billing/${providerId}/summary-details`, params);
      } catch (error) {
        console.error('Billing summary details error:', error);
        throw error;
      }
    }
  },

  ohioCounty: {
    getAll: async () => {
      try {
        return httpClient.get('/api/v1/ohio-county');
      } catch (error) {
        console.error('Ohio county error:', error);
        throw error;
      }
    },

    get: async (id: number) => {
      try {
        return httpClient.get(`/api/v1/ohio-county/${id}`);
      } catch (error) {
        console.error('Ohio county error:', error);
        throw error;
      }
    },

    getByName: async (name: string) => {
      try {
        return httpClient.get(`/api/v1/ohio-county/name/${name}`);
      } catch (error) {
        console.error('Ohio county error:', error);
        throw error;
      }
    },

    getByCODB: async (codb: number) => {
      try {
        return httpClient.get(`/api/v1/ohio-county/codb/${codb}`);
      } catch (error) {
        console.error('Ohio county error:', error);
        throw error;
      }
    }
  },

  serviceUnitConversion: {
    getAll: async () => {
      try {
        return httpClient.get('/api/v1/service-unit-conversion?limit=1000');
      } catch (error) {
        console.error('Service unit conversion error:', error);
        throw error;
      }
    },

    get: async (id: number) => {
      try {
        return httpClient.get(`/api/v1/service-unit-conversion/${id}`);
      } catch (error) {
        console.error('Service unit conversion error:', error);
        throw error;
      }
    },

    getByMinutes: async (minutes: number) => {
      try {
        return httpClient.get(`/api/v1/service-unit-conversion/minutes/${minutes}`);
      } catch (error) {
        console.error('Service unit conversion error:', error);
        throw error;
      }
    }
  },

  scheduleOverlap: {
    check: async (params: {
      client_id: number;
      start: string;
      end: string;
      provider_id?: number;
      exclude_visit_id?: number;
      exclude_transportation_id?: number;
    }) => {
      const sp = new URLSearchParams();
      sp.set('client_id', String(params.client_id));
      sp.set('start', params.start);
      sp.set('end', params.end);
      if (params.provider_id != null) {
        sp.set('provider_id', String(params.provider_id));
      }
      if (params.exclude_visit_id != null) {
        sp.set('exclude_visit_id', String(params.exclude_visit_id));
      }
      if (params.exclude_transportation_id != null) {
        sp.set('exclude_transportation_id', String(params.exclude_transportation_id));
      }
      return httpClient.get(`/api/v1/schedule-overlap/check?${sp.toString()}`);
    },
  },

  ui: {
    list: async (params: {
      provider_id?: number;
      client_id?: number;
      visit_id?: number;
      transportation_id?: number;
      mui?: boolean;
    }) => {
      const sp = new URLSearchParams();
      if (params.provider_id != null) sp.set('provider_id', String(params.provider_id));
      if (params.client_id != null) sp.set('client_id', String(params.client_id));
      if (params.visit_id != null) sp.set('visit_id', String(params.visit_id));
      if (params.transportation_id != null) sp.set('transportation_id', String(params.transportation_id));
      if (params.mui != null) sp.set('mui', String(params.mui));
      const qs = sp.toString();
      return httpClient.get(`/api/v1/ui${qs ? `?${qs}` : ''}`);
    },

    create: async (data: import('../types').UiCreate) => {
      return httpClient.post('/api/v1/ui', data);
    },

    update: async (id: number, data: import('../types').UiUpdate) => {
      return httpClient.put(`/api/v1/ui/${id}`, data);
    },

    delete: async (id: number) => {
      return httpClient.delete(`/api/v1/ui/${id}`);
    },
  },

  complianceReports: {
    listTemplates: async () => {
      return httpClient.get('/api/v1/compliance-reports/templates');
    },

    downloadUnusualIncidentLog: async (params: {
      provider_id: number;
      start_date: string;
      end_date: string;
      client_id?: number;
    }) => {
      const sp = new URLSearchParams();
      sp.set('provider_id', String(params.provider_id));
      sp.set('start_date', params.start_date);
      sp.set('end_date', params.end_date);
      if (params.client_id != null) {
        sp.set('client_id', String(params.client_id));
      }
      return httpClient.getBlob(`/api/v1/compliance-reports/unusual-incident-log?${sp.toString()}`);
    },

    downloadHpcServiceDelivery: async (params: {
      provider_id: number;
      client_id: number;
      year: number;
      month: number;
      isp_id?: number;
    }) => {
      const sp = new URLSearchParams();
      sp.set('provider_id', String(params.provider_id));
      sp.set('client_id', String(params.client_id));
      sp.set('year', String(params.year));
      sp.set('month', String(params.month));
      if (params.isp_id != null) {
        sp.set('isp_id', String(params.isp_id));
      }
      return httpClient.getBlob(`/api/v1/compliance-reports/hpc-service-delivery?${sp.toString()}`);
    },

    downloadHpcOutcomeDocumentation: async (params: {
      provider_id: number;
      client_id: number;
      isp_id: number;
    }) => {
      const sp = new URLSearchParams();
      sp.set('provider_id', String(params.provider_id));
      sp.set('client_id', String(params.client_id));
      sp.set('isp_id', String(params.isp_id));
      return httpClient.getBlob(`/api/v1/compliance-reports/hpc-outcome-documentation?${sp.toString()}`);
    },
  },
}; 